import { useMemo } from 'react';

import { Chip, Stack, Tooltip, Typography } from '@mui/material';
import { GridColDef } from '@mui/x-data-grid-pro';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router';

import { PROJECT_STATUSES } from '../../../constants';
import { getProjectRecommendations } from '../../ProjectDetails/helpers';
import styles from '../ProjectTable.module.scss';
import { dataGridCellFontFamilyTabularNumsClassName } from '@/assets/themes/components/MuiDataGrid';
import { Boolean } from '@/components/cellRenderers';
import { Abbr } from '@/components/ui/Abbr';
import { Icon } from '@/components/ui/Icon';
import {
  StatusIndicator,
  StatusIndicatorVariant,
} from '@/components/ui/StatusIndicator/StatusIndicator';
import { legacyRoutes } from '@/constants/legacyRoutes';
import { routes } from '@/constants/routes';
import { uiIcons } from '@/constants/uiIcons';
import { useHasNewProjectManagement } from '@/hooks/featureFlags';
import type { ColumnWidths } from '@/hooks/grid';
import { useLocaleStore } from '@/stores/locale';
import { createDateColumn } from '@/utils/dataGrid';
import { pascalToCamelCase } from '@/utils/string';

interface UseColumnsOptions {
  columnWidths?: ColumnWidths;
  onViewDetails?: (projectId: string) => void;
}

export const useColumns = ({
  columnWidths,
  onViewDetails,
}: UseColumnsOptions = {}): GridColDef[] => {
  const { t } = useTranslation('portfolio');
  const { lang } = useLocaleStore();
  const hasNewProjectManagement = useHasNewProjectManagement();

  return useMemo(() => {
    const baseColumns: GridColDef[] = [
      {
        field: 'HealthStatusName',
        headerName: t('columns.Health'),
        display: 'flex',
        align: 'center',
        width: 70,
        renderCell: (params) => {
          if (!params.value) {
            return (
              <Tooltip title={t('tooltip.HealthStatusPending')}>
                <Icon name={uiIcons.healthStatus.pending} color="secondary" />
              </Tooltip>
            );
          }
          const recommendations = getProjectRecommendations(
            params.row,
            t,
            hasNewProjectManagement,
          );

          // Convert PascalCase from backend (AtRisk, OnTrack) to camelCase for component (atRisk, onTrack)
          const variant = pascalToCamelCase(
            params.value as string,
          ) as StatusIndicatorVariant;

          const tooltipContent = recommendations.length > 0 && (
            <Stack gap={2} alignItems="flex-start" className={styles.Tooltip}>
              <StatusIndicator variant={variant} />
              <Stack
                gap={2}
                component="ul"
                className={styles.RecommendationList}
              >
                {recommendations.map((rec, index) => (
                  <Stack key={index} alignItems="flex-start" component="li">
                    <Typography component="p" variant="label">
                      {rec.title}
                    </Typography>
                  </Stack>
                ))}
              </Stack>
            </Stack>
          );
          return (
            <StatusIndicator
              tooltip={tooltipContent}
              iconOnly
              variant={variant}
              showCursorPointer={recommendations.length > 0}
            />
          );
        },
      },
      {
        field: 'Name',
        headerName: t('columns.ProjectName'),
        flex: 1,
        minWidth: 300,
        hideable: false,
        renderCell: (params) => (
          <Link
            to={
              hasNewProjectManagement
                ? routes.projects.planning(params.row.SourceId)
                : legacyRoutes.projectManagement.planning(params.row.SourceId)
            }
            data-automation-id="project-portfolio-table-project-link"
          >
            {params.value}
          </Link>
        ),
      },
      {
        field: 'Number',
        type: 'number',
        cellClassName: dataGridCellFontFamilyTabularNumsClassName,
        headerName: t('columns.ProjectNumberShort'),
        renderHeader: (params) => (
          <Typography component="span" variant="label">
            <Abbr
              shortText={params.colDef.headerName ?? 'No.'}
              longText={t('columns.ProjectNumberLong')}
            />
          </Typography>
        ),
        width: 100,
      },
      {
        field: 'CustomerName',
        headerName: t('columns.CustomerName'),
        flex: 1,
        minWidth: 180,
        renderCell: (params) => {
          const customerName = params.value as string;
          const isInternalProject = params.row.IsInternalProject as boolean;
          if (isInternalProject) {
            return (
              <>
                {customerName}{' '}
                <Typography component="span" color="textDisabled">
                  ({t('columns.IsInternalProject')})
                </Typography>
              </>
            );
          }
          return customerName;
        },
      },
      {
        field: 'CustomerNumber',
        type: 'number',
        cellClassName: dataGridCellFontFamilyTabularNumsClassName,
        headerName: t('columns.CustomerNumberShort'),
        renderHeader: (params) => (
          <Typography component="span" variant="label">
            <Abbr
              shortText={params.colDef.headerName ?? 'No.'}
              longText={t('columns.CustomerNumberLong')}
            />
          </Typography>
        ),
        width: 120,
      },
      {
        field: 'ProjectManagerName',
        headerName: t('columns.ProjectManagerName'),
        flex: 1,
        minWidth: 180,
      },
      {
        field: 'AccountManagerName',
        headerName: t('columns.AccountManagerName'),
        flex: 1,
        minWidth: 180,
      },
      {
        field: 'Category',
        headerName: t('columns.Category'),
        flex: 1,
        minWidth: 180,
      },
      {
        field: 'TypeName',
        headerName: t('columns.Type'),
        flex: 1,
        minWidth: 180,
      },
      {
        field: 'StatusName',
        headerName: t('columns.StatusName'),
        flex: 1,
        minWidth: 180,
        // Sort mapping configured in PORTFOLIO_SORT_FIELD_MAP: StatusName → StatusSortOrder
        renderCell: (params) => {
          const statusValue = params.value as string;
          // Check if the status is one of our known PROJECT_STATUSES values
          const isKnownStatus = (
            PROJECT_STATUSES as readonly string[]
          ).includes(statusValue);
          // Use translation if available, otherwise show raw value

          return (
            <Chip
              variant="outlined"
              color="secondary"
              size="small"
              label={
                // eslint-disable-next-line @typescript-eslint/no-explicit-any
                isKnownStatus ? t(`enums.${statusValue}` as any) : statusValue
              }
              monoText
            />
          );
        },
      },
      {
        field: 'PartnerName',
        headerName: t('columns.PartnerName'),
        flex: 1,
        minWidth: 180,
      },
      {
        field: 'LegalEntityName',
        headerName: t('columns.LegalEntityName'),
        flex: 1,
        minWidth: 180,
      },
      {
        field: 'DepartmentName',
        headerName: t('columns.DepartmentName'),
        flex: 1,
        minWidth: 180,
      },
      {
        field: 'PoNumber',
        type: 'number',
        cellClassName: dataGridCellFontFamilyTabularNumsClassName,
        headerName: t('columns.PurchaseOrderNumberShort'),
        renderHeader: (params) => (
          <Typography component="span" variant="label">
            <Abbr
              shortText={params.colDef.headerName ?? 'P.O. no.'}
              longText={t('columns.PurchaseOrderNumberLong')}
            />
          </Typography>
        ),
        flex: 1,
        minWidth: 180,
      },
      {
        field: 'StartDate',
        headerName: t('columns.StartDate'),
        ...createDateColumn(lang),
      },
      {
        field: 'EndDate',
        headerName: t('columns.EndDate'),
        ...createDateColumn(lang),
      },
      {
        field: 'LastTimeRegistrationDate',
        headerName: t('columns.LastTimeRegistrationDate'),
        ...createDateColumn(lang),
      },

      // {
      //   field: 'HoursRegistered',
      //   headerName: t('columns.Hours'),
      //   flex: 1,
      //   minWidth: 120,
      //   headerAlign: 'right',
      //   align: 'right',
      //   valueGetter: (_value, row) => row.HoursRegistered ?? 0,
      //   renderCell: (params) => {
      //     const registered = params.row.HoursRegistered ?? 0;
      //     const budget = params.row.HoursBudget ?? 0;
      //     return `${registered}/${budget}`;
      //   },
      // },
      // {
      //   field: 'TotalTasks',
      //   headerName: t('columns.TotalTasks'),
      //   flex: 1,
      //   minWidth: 80,
      // },
      // {
      //   field: 'OpenTasks',
      //   headerName: t('columns.OpenTasks'),
      //   flex: 1,
      //   minWidth: 80,
      // },

      {
        field: 'IsInternalProject',
        headerName: t('columns.IsInternalProject'),
        minWidth: 80,
        type: 'boolean',
        renderCell: (params) => (
          <Boolean value={params.value} showFalse={false} />
        ),
      },
      {
        field: 'IsActive',
        headerName: t('columns.IsActive'),
        minWidth: 80,
        type: 'boolean',
        renderCell: (params) => <Boolean value={params.value} />,
      },
    ];

    // Apply stored widths to columns if available
    if (columnWidths) {
      return baseColumns.map((col) => {
        const storedWidth = columnWidths[col.field];
        if (
          storedWidth !== undefined &&
          Number.isFinite(storedWidth) &&
          storedWidth >= 0
        ) {
          // Remove flex and apply stored width
          const { flex: _flex, ...colWithoutFlex } = col;
          return { ...colWithoutFlex, width: storedWidth };
        }
        return col;
      });
    }

    return baseColumns;
  }, [t, columnWidths, lang, onViewDetails, hasNewProjectManagement]);
};
